import { Button, Modal } from "../../../components";
import { CalendarIcon, InfoIcon } from "../../../assets/icons";
import { formatISOString, isFuture } from "../../../utils";
import { FC, Fragment, useCallback, useMemo, useState } from "react";
import {
  FacilityTab,
  LoanApplication,
  LOSPlatform,
  useLoanDashboard,
} from "../../../contexts";
import {
  AdditionalInfo,
  AdditionalInfoType,
  ShareholderEntityType,
  LoanApplicationStatus,
  LoanProductType,
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusLabelsForCreditLine,
  LoanApplicationCaseStatusLabels,
  LoanApplicationStatusLabels,
  MediaPurpose,
} from "../../../hooks/api/types";
import {
  useUpdateCompanyShareholder,
  useUpdateIndividualShareholder,
  useUpdateLoanAdditionalInfo,
} from "../../../hooks";
import MissingDocumentsUpload from "./MissingInformation/MissingDocumentUpload";
import AddShareholder from "../../LoanApplicationSteps/OwnersInformation/AddShareholder";
import FinanceInformationSelect from "./MissingInformation/FinanceInformationSelect";
import LinkBankAccount from "./MissingInformation/LinkBankAccount";
import { ShareholderFields } from "../../LoanApplicationSteps/OwnersInformation/service";
import { AWAITABLE_STATUSES } from "./CONSTANTS";
import { useDpConfig } from "../../../contexts/dpConfig";
import { LoanProduct } from "../../../contexts/dpConfig/types";
import { getVendorNature } from "../../LoanApplicationSteps/VendorInformation/utils";
import { shouldIssueDrawdown } from "../LineOfCredit/service";
type LoanCardActionsProps = {
  loan: LoanApplication;
  onApplyDrawdown?: () => void; // new optional prop for drawdown
  drawdownError?: string | null; // when set, disables Apply for drawdown
};

const nextPaymentCopyMapping = new Map([
  [
    LoanProductType.REVENUE_BASED_FINANCING.toString(),
    "The next payment is due on",
  ],
  [LoanProductType.PAYABLE_FINANCING.toString(), "Your payment due date is"],
  [LoanProductType.INVOICE_DISCOUNTING.toString(), "Your payment due date is"],
  [LoanProductType.FIXED_TERM.toString(), "Your payment due date is"],
]);

const getNextPaymentCopy = (loan: LoanApplication) => {
  return (
    <div>
      {nextPaymentCopyMapping.get(loan.loanType)}{" "}
      {formatISOString(loan?.nextPaymentDate || new Date().toISOString())}
    </div>
  );
};

const loanInfoRequiredActionMap: Map<AdditionalInfoType, string> = new Map([
  [
    AdditionalInfoType.SHAREHOLDERS_CONTACTS,
    "Please add email address and phone number of Shareholder Name.",
  ],
  [AdditionalInfoType.DOC_TLN, "Please upload your latest Trade License."],
  [AdditionalInfoType.DOC_POA, "Please upload your latest Power of Attorney."],
  [
    AdditionalInfoType.DOC_MOA,
    "Please upload your latest Memorandum of Association.",
  ],
  [AdditionalInfoType.DOC_VAT, "Please upload your latest VAT Statements."],
  [
    AdditionalInfoType.DOC_BANK,
    "Please upload your latest bank statement or connect your bank account for fast approval.",
  ],
  [AdditionalInfoType.DOC_SALES_STATEMENT, "Please provide sales statements."],
  [
    AdditionalInfoType.DOC_POS_SALES_STATEMENT,
    "Please provide sales statements.",
  ],
  [AdditionalInfoType.DOC_IBAN_LETTER, "Please provide iban letter."],
  [AdditionalInfoType.DOC_VAT_CERTIFICATE, "Please provide VAT certificate."],
  [AdditionalInfoType.DOC_OTHER, "Please provide additional documents."],
  [AdditionalInfoType.DOC_SALES_INVOICE, "Please provide sales Invoices."],
]);

const dontDisplayAdditionalInfo = [AdditionalInfoType.SHAREHOLDERS_AECB];

const getRequiredDocumentMessage = (info: AdditionalInfo) => {
  if (info.type == AdditionalInfoType.DOC_OTHER) return info.hint;
  return loanInfoRequiredActionMap.get(info.type);
};

function getProductByType(
  products: LoanProduct[],
  loanProductType: LoanProductType
): LoanProduct | undefined {
  return products.find((product) => product.type === loanProductType);
}

export const LoanCardActions: FC<LoanCardActionsProps> = ({
  loan,
  onApplyDrawdown,
  drawdownError,
}) => {
  const {
    state,
    actions: { update, refetch },
  } = useLoanDashboard();

  const {
    state: { products, name: dpName },
  } = useDpConfig();

  const product = useMemo(() => {
    return getProductByType(products, loan.loanType as LoanProductType);
  }, [products, loan.loanType]);
  const disablePaymentScheduleFlag = !!product?.config?.disablePaymentSchedule;
  const showLOCProductsPaymentSchedule =
    !!product?.config?.showLOCProductsPaymentSchedule;
  const isCreditFacility =
    loan.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
    loan.loanType === LoanProductType.LOC_PAYABLE_FINANCING;

  const showPaymentProcessThroughDPText =
    product?.uiLocalizationData?.smeDashboard
      ?.showPaymentProcessThroughDPText ?? false;
  const { mutateAsync: updateCompanyShareholder } =
    useUpdateCompanyShareholder();
  const { mutateAsync: updateIndividualShareholder } =
    useUpdateIndividualShareholder();

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalContent, setActionModalContent] = useState<any>();
  const handleOpenSchedule = useCallback(
    (loanId: string) => {
      update({ currentLoanApplicationId: loanId, showPaymentSchedule: true });
    },
    [update]
  );
  const handleOpenPaymentHistory = useCallback(
    (loanId: string) => {
      update({ currentLoanApplicationId: loanId, showPaymentHistory: true });
    },
    [update]
  );
  const handleOpenTransactionsHistoryForPosRB = async (
    currentLoanId: string
  ) => {
    await update({
      currentLoanApplicationId: currentLoanId,
      showPaymentDetails: false,
      showPaymentHistory: false,
      showTransactionHistory: true,
    });
  };
  const { mutateAsync: updateInfo } = useUpdateLoanAdditionalInfo();

  const showScheduleButton = useMemo(() => {
    // Hide schedule button entirely for credit facility products
    if (
      loan.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
      loan.loanType === LoanProductType.LOC_PAYABLE_FINANCING
    ) {
      return false;
    }

    // For all other loan types, use normal conditions
    return (
      !disablePaymentScheduleFlag &&
      !(loan.loanType === LoanProductType.POS_REVENUE_BASED_FINANCING) &&
      (loan.status === LoanApplicationStatus.ACTIVE ||
        loan.caseStatus === LoanApplicationCaseStatus.ACTIVE)
    );
  }, [disablePaymentScheduleFlag, loan, showLOCProductsPaymentSchedule]);

  const showDrawdownButton = useMemo(() => {
    if (!isCreditFacility) return false;
    if (loan.losPlatform !== LOSPlatform.CREDIBLEX) return false;
    return loan.caseStatus != null && shouldIssueDrawdown(loan.caseStatus);
  }, [isCreditFacility, loan.losPlatform, loan.caseStatus]);

  const isDrawdownDisabled =
    loan.drawdownEligibility != null && !loan.drawdownEligibility.isValid;

  const showPaymentHistoryButton = useMemo(() => {
    return (
      loan.status === LoanApplicationStatus.ACTIVE &&
      loan.loanType == LoanProductType.REVENUE_BASED_FINANCING &&
      loan.losPlatform === LOSPlatform.BIZ2X // Not available for Crediblex los RN
    );
  }, [loan]);

  const showTransactionsHistoryButtonForPosRbf = useMemo(() => {
    return (
      loan.status === LoanApplicationStatus.ACTIVE &&
      loan.loanType == LoanProductType.POS_REVENUE_BASED_FINANCING
    );
  }, [loan]);

  const handleShareholderModalSubmit = async (
    data: ShareholderFields,
    info: AdditionalInfo
  ) => {
    if (info.userId) {
      if (data.__entityType === ShareholderEntityType.INDIVIDUAL) {
        await updateIndividualShareholder({
          id: info.userId.toString(),
          ...data,
        });
        await updateMissingInfo(info);
      } else {
        await updateCompanyShareholder({
          id: info.userId.toString(),
          ...data,
        });
        await updateMissingInfo(info);
      }
      refetch();
      setActionModalOpen(false);
    }
  };

  const handleModalSubmit = (
    requestedDocumentRes: AdditionalInfo[],
    loanApplicationId: string
  ) => {
    const updatedLoans = state.loans.map((loan) =>
      loan.id === loanApplicationId
        ? { ...loan, additionalInfo: requestedDocumentRes }
        : loan
    );
    update({ loans: updatedLoans });
    setActionModalOpen(false);
  };

  const navigateToAdditionalInfoScreen = async (loanApplicationId: string) => {
    await update({
      currentLoanApplicationId: loanApplicationId,
      currentLoanApplication: loan,
      showFacilityOverview: true,
      showAdditionalInfoRequired: true,
      showPaymentDetails: false,
      showPaymentHistory: false,
      showPaymentSchedule: false,
      showTransactionHistory: false,
      showDrawdownForm: false,
    });
  };

  const navigateToVendorInfoScreen = async (loanApplicationId: string) => {
    await update({
      currentLoanApplicationId: loanApplicationId,
      currentLoanApplication: loan,
      showFacilityOverview: true,
      showAdditionalInfoRequired: true,
      facilityActiveTab: FacilityTab.VENDORS,
      showPaymentDetails: false,
      showPaymentHistory: false,
      showPaymentSchedule: false,
      showTransactionHistory: false,
      showDrawdownForm: false,
    });
  };

  const handleFinanceInformationSelect = (
    selected: "connect" | "upload" | null,
    info: AdditionalInfo
  ) => {
    let modalContent = null;
    setActionModalOpen(false);
    switch (selected) {
      case "connect":
        modalContent = (
          <LinkBankAccount
            loanApplicationId={info.loanApplicationId}
            onCancel={() => setActionModalOpen(false)}
            onSubmit={() => setActionModalOpen(false)}
          />
        );
        setActionModalContent(modalContent);
        setActionModalOpen(true);
        break;
      case "upload":
        modalContent = (
          <MissingDocumentsUpload
            subtitle={"Bank Statements"}
            documentType={MediaPurpose.BANK_STATEMENT}
            requestedDocument={info}
            onSubmit={(data) => handleModalSubmit(data, info.loanApplicationId)}
            onClose={() => setActionModalOpen(false)}
          />
        );
        setActionModalContent(modalContent);
        setActionModalOpen(true);
        break;
    }
  };

  const handleActionClick = async (info: AdditionalInfo) => {
    await update({ currentLoanApplicationId: info.loanApplicationId });
    let modalContent = null;
    let subtitle = "";
    let documentType: MediaPurpose | null = null;
    switch (info.type) {
      case AdditionalInfoType.DOC_VAT:
        subtitle = "VAT statement";
        documentType = MediaPurpose.VAT_STATEMENT;
        break;
      case AdditionalInfoType.DOC_MOA:
        subtitle = "MOA";
        documentType = MediaPurpose.MEMORANDUM_OF_ASSOCIATION;
        break;
      case AdditionalInfoType.DOC_POA:
        subtitle = "POA";
        documentType = MediaPurpose.POWER_OF_ATTORNEY;
        break;
      case AdditionalInfoType.DOC_BANK:
        modalContent = (
          <FinanceInformationSelect
            title={"Action required"}
            subtitle={
              "Please upload your bank statement or try to connect your bank for faster approval"
            }
            onSelect={(tab) => handleFinanceInformationSelect(tab, info)}
            onClose={() => setActionModalOpen(false)}
          />
        );
        break;
      case AdditionalInfoType.DOC_TLN:
        subtitle = "Trade Licence";
        documentType = MediaPurpose.TRADE_LICENSE;
        break;
      case AdditionalInfoType.DOC_OTHER:
        subtitle = info.hint!;
        documentType = MediaPurpose.GENERIC_DOCUMENT;
        break;
      case AdditionalInfoType.DOC_SALES_STATEMENT:
        subtitle = "Sales Statement";
        documentType = MediaPurpose.SALES_STATEMENT;
        break;
      case AdditionalInfoType.DOC_POS_SALES_STATEMENT:
        subtitle = "Sales Statement";
        documentType = MediaPurpose.POS_SALES_STATEMENT;
        break;
      case AdditionalInfoType.DOC_VAT_CERTIFICATE:
        subtitle = "VAT Certificate";
        documentType = MediaPurpose.VAT_CERTIFICATE;
        break;
      case AdditionalInfoType.DOC_IBAN_LETTER:
        subtitle = "IBAN Letter";
        documentType = MediaPurpose.IBAN_LETTER;
        break;
      case AdditionalInfoType.DOC_SALES_INVOICE:
        subtitle = "Sales Invoice";
        documentType = MediaPurpose.INVOICE;
        break;
      case AdditionalInfoType.SHAREHOLDERS_CONTACTS:
        modalContent = (
          <AddShareholder
            title={"Action Required"}
            shareholder={info.shareholder}
            onSubmit={(data) => handleShareholderModalSubmit(data, info)}
            onCancel={() => setActionModalOpen(false)}
            modal
          />
        );
        break;
    }
    if (modalContent) {
      setActionModalContent(modalContent);
      setActionModalOpen(true);
    } else if (subtitle && documentType) {
      modalContent = (
        <MissingDocumentsUpload
          subtitle={subtitle}
          documentType={documentType}
          requestedDocument={info}
          onSubmit={(data) => handleModalSubmit(data, info.loanApplicationId)}
          onClose={() => setActionModalOpen(false)}
        />
      );
      setActionModalContent(modalContent);
      setActionModalOpen(true);
    }
  };
  const updateMissingInfo = (
    requestedDocument: AdditionalInfo,
    values?: string[]
  ) => {
    return updateInfo({
      id: requestedDocument.loanApplicationId,
      infoType: requestedDocument.type,
      userId: requestedDocument.userId,
      values,
    });
  };

  const renderLoanActions = () => {
    const isAdditionalInfoRequested =
      loan.losPlatform === LOSPlatform.CREDIBLEX
        ? loan.caseStatus === LoanApplicationCaseStatus.ADDITIONAL_INFO_REQUIRED
        : loan.status === LoanApplicationStatus.ADDITIONAL_INFO_REQUESTED;

    const showAdditionalInfo =
      isAdditionalInfoRequested &&
      loan.additionalInfo &&
      loan.additionalInfo?.length > 0;
    const showVendorInfo =
      loan.requiredVendorInformation &&
      loan.requiredVendorInformation?.length > 0;

    if (showAdditionalInfo || showVendorInfo) {
      if (isCreditFacility) {
        return (
          <>
            {showAdditionalInfo && (
              <div className="cx-flex cx-w-full cx-pr-10 cx-pl-6 cx-py-1 cx-justify-between cx-items-center cx-border-neutral-light ">
                <div className="cx-text-error-dark cx-text-sm cx-font-normal cx-flex cx-items-center cx-gap-2">
                  <span>
                    <InfoIcon width={16} height={16} />
                  </span>
                  Additional information is required to continue processing your
                  application.
                </div>
                <div className="cx-text-sm">
                  <Button
                    slim
                    label="Take action"
                    onClick={() => navigateToAdditionalInfoScreen(loan.id)}
                  />
                </div>
              </div>
            )}
            {showVendorInfo && (
              <div className="cx-flex cx-w-full cx-pr-10 cx-pl-6 cx-py-2 cx-justify-between cx-items-center cx-border-neutral-light ">
                <div className="cx-text-error-dark cx-text-sm cx-font-normal cx-flex cx-items-center cx-gap-2">
                  <span>
                    <InfoIcon width={16} height={16} />
                  </span>
                  Some {getVendorNature(loan?.loanType)}s require your
                  attention.
                </div>
                <div className="cx-text-sm">
                  <Button
                    slim
                    label="Take action"
                    onClick={() => navigateToVendorInfoScreen(loan.id)}
                  />
                </div>
              </div>
            )}
          </>
        );
      } else {
        return (
          <Fragment>
            {loan.additionalInfo
              .filter((info) => !dontDisplayAdditionalInfo.includes(info.type))
              .map((info: any) => (
                <div
                  key={info.id}
                  className="cx-flex cx-w-full cx-px-6 cx-py-2 cx-justify-between cx-items-center"
                >
                  <div className="cx-text-error-dark cx-text-sm cx-font-normal cx-flex cx-gap-1">
                    <span>
                      <InfoIcon width={16} height={16} color="#0064FF" />
                    </span>
                    {getRequiredDocumentMessage(info)}
                  </div>
                  <div className="cx-text-sm">
                    <Button
                      slim
                      label="Take Action"
                      onClick={() => handleActionClick(info)}
                    />
                  </div>
                </div>
              ))}
          </Fragment>
        );
      }
    }

    // Credit facility specific messages
    if (isCreditFacility) {
      let creditFacilityMessage: string | null = null;
      const status =
        loan.losPlatform === LOSPlatform.CREDIBLEX
          ? isCreditFacility
            ? LoanApplicationCaseStatusLabelsForCreditLine[
                loan.caseStatus as LoanApplicationCaseStatus
              ]
            : LoanApplicationCaseStatusLabels[
                loan.caseStatus as LoanApplicationCaseStatus
              ]
          : LoanApplicationStatusLabels[loan.status as LoanApplicationStatus];
      switch (status?.label) {
        case "Active":
        case "Overdue":
          creditFacilityMessage = "You can apply drawdown from here directly";
          break;
        case "Submitted":
          creditFacilityMessage =
            "Your application is submitted successfully, we will get back to you soon";
          break;
        case "In review":
          creditFacilityMessage =
            "Your application is in review, we will get back to you soon";
          break;
        case "Offer sent":
          creditFacilityMessage =
            "An offer has been sent to you, please accept to proceed further";
          break;
        case "Offer rejected":
          creditFacilityMessage =
            "You rejected the offer, please contact the team for a counter offer";
          break;
        case "Counter offer sent":
          creditFacilityMessage =
            "A counter offer has been sent to you. Please check your email to accept the offer";
          break;
        case "Awaiting Disbursement":
        case "Awaiting Activation":
          creditFacilityMessage =
            "Your funds will be disbursed in 3 to 5 working days";
          break;
        case "Closed":
          creditFacilityMessage =
            "This loan has been paid and closed successfully";
          break;
        case "Contract pending signature":
          creditFacilityMessage =
            "Please review the contract shared with you via email.";
          break;
      }
      if (creditFacilityMessage) {
        return (
          <div className="cx-flex cx-w-full cx-px-6 cx-items-center">
            <div className="cx-flex cx-w-full cx-items-center cx-justify-between">
              <div className="cx-text-text-tertiary cx-text-sm cx-font-normal cx-flex cx-items-center cx-gap-2 cx-flex-wrap">
                <InfoIcon width={16} height={16} color="#0064FF" />
                <span>{creditFacilityMessage}</span>
              </div>
              {showDrawdownButton && (
                <Button
                  slim
                  label="Apply for new drawdown"
                  onClick={onApplyDrawdown}
                  disabled={isDrawdownDisabled || !!drawdownError}
                  className="cx-h-9 cx-py-1"
                />
              )}
            </div>
          </div>
        );
      }
    }

    return AWAITABLE_STATUSES.includes(loan.status) &&
      loan.losPlatform !== LOSPlatform.CREDIBLEX ? (
      <div className="cx-flex cx-w-full cx-px-6 cx-items-center">
        <div className="cx-text-text-tertiary cx-text-sm cx-font-normal cx-flex cx-items-center cx-gap-1">
          <InfoIcon width={16} height={16} color="#0064FF" />
          <span>Your loan application is under review</span>
        </div>
      </div>
    ) : loan.status === LoanApplicationStatus.ACTIVE &&
      showPaymentProcessThroughDPText ? (
      <div className="cx-flex cx-w-full cx-px-6 cx-items-center">
        <div className="cx-text-text-tertiary cx-text-sm cx-font-normal cx-flex cx-items-center cx-gap-1">
          <InfoIcon width={16} height={16} color="#0064FF" />
          <span>
            Your payment will be automatically processed through {dpName}
          </span>
        </div>
      </div>
    ) : (loan.status === LoanApplicationStatus.OFFER_ACCEPTED ||
        loan.status === LoanApplicationStatus.WAITING_FOR_DISBURSEMENT) &&
      loan.loanType === LoanProductType.POS_REVENUE_BASED_FINANCING ? (
      <div className="cx-flex cx-w-full cx-px-6 cx-items-center">
        <div className="cx-text-text-secondary cx-text-sm cx-font-normal cx-flex cx-items-center cx-gap-1">
          <InfoIcon width={16} height={16} color="#0064FF" />
          <span>
            The upfront fee will be deducted from the loan amount prior to
            disbursal.
          </span>
        </div>
      </div>
    ) : null;
  };

  return (
    <div>
      <div className="cx-w-full sm:cx-flex cx-flex-row cx-items-center cx-pl-3 cx-pr-6 cx-pt-4 cx-border-t-0.5 cx-border-neutral-light">
        <div className="cx-grow cx-flex cx-flex-row cx-items-center">
          {loan.status == LoanApplicationStatus.ACTIVE &&
            isFuture(loan?.nextPaymentDate) &&
            !isCreditFacility && (
              <>
                <div className="cx-grow cx-text-text-secondary cx-font-medium cx-text-sm">
                  {getNextPaymentCopy(loan)}
                </div>
              </>
            )}
        </div>
        <div className="cx-w-full cx-flex cx-flex-row cx-justify-center sm:cx-w-auto sm:cx-justify-start cx-gap-x-4">
          {showScheduleButton && (
            <Button
              slim={true}
              label={
                <div className="cx-flex cx-flex-row cx-items-center cx-gap-2.5">
                  <CalendarIcon width={16} height={16} stroke="#fff" />
                  Payments schedule
                </div>
              }
              onClick={() => handleOpenSchedule(loan.id)}
            />
          )}
          {/* Removed standalone drawdown button; now inline with creditFacilityMessage */}
          {showPaymentHistoryButton && (
            <div className="sm:cx-flex cx-text-s cx-font-normal cx">
              <Button
                label={"Payment History"}
                type="button"
                slim={true}
                onClick={() => handleOpenPaymentHistory(loan.id)}
                outlined
                className="cx-px-1 cx-border-1.5"
              />
            </div>
          )}
          {showTransactionsHistoryButtonForPosRbf && (
            <div className="sm:cx-flex cx-text-s cx-font-normal cx">
              <Button
                label={"Transactions History"}
                type="button"
                slim={true}
                onClick={() => handleOpenTransactionsHistoryForPosRB(loan.id)}
                outlined
                className="cx-px-1 cx-border-1.5"
              />
            </div>
          )}
        </div>
      </div>
      <div>{renderLoanActions()}</div>
      <Modal isOpen={actionModalOpen} onClose={() => setActionModalOpen(false)}>
        {actionModalContent}
      </Modal>
    </div>
  );
};

export default LoanCardActions;
